import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { getAnswer } from './puzzleApi';
import CaseNotice from './CaseNotice';
import CaseJoin from './CaseJoin';
import ShareButton from './ShareButton';
import { caseNumber, plainTitle, renderEmphasis, renderTitle, difficultyLabel } from './puzzleText';
import './CaseFile.css';

const fontsUrl = 'https://fonts.googleapis.com/css2?family=Special+Elite&family=Libre+Franklin:ital,wght@0,400;0,600;0,800;1,400&display=swap';

const CaseAnswer = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const guid = searchParams.get('c');

  const [state, setState] = useState({ status: guid ? 'loading' : 'error', code: 'not_found' });

  useEffect(() => {
    if (!guid) {
      return undefined;
    }

    let cancelled = false;

    setState({ status: 'loading' });

    getAnswer(guid)
      .then((data) => {
        if (!cancelled) {
          setState({ status: 'ready', data });
          window.scrollTo(0, 0);
        }
      })
      .catch((error) => {
        if (cancelled) {
          return;
        }

        // Nobody reaches the solution without finishing the game first: send them back to it.
        if (error.code === 'not_played') {
          navigate(`/puzzle?c=${encodeURIComponent(guid)}`, { replace: true });
          return;
        }

        setState({ status: 'error', code: error.code || 'server_error' });
      });

    return () => {
      cancelled = true;
    };
  }, [guid, navigate]);

  if (state.status === 'loading') {
    return <CaseNotice code="loading" />;
  }

  if (state.status === 'error') {
    return <CaseNotice code={state.code} />;
  }

  const { case: puzzle, result, verdict, streakMessage, solution, player } = state.data;
  const shareProps = {
    title: puzzle.title,
    teaser: puzzle.shareTeaser,
    difficulty: puzzle.difficulty,
    wasCorrect: result.wasCorrect,
    hintsUsed: result.hintsUsed,
    points: result.points
  };

  return (
    <div className="case-file-page">
      <Helmet>
        <title>{`The Solution: ${plainTitle(puzzle.title)} | Jerry Evanoff`}</title>
        <meta name="robots" content="noindex" />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link rel="stylesheet" href={fontsUrl} />
      </Helmet>

      <main className="cf-folder">
        <span className="cf-tab">CASE FILE · No. {caseNumber(puzzle.number)}</span>
        <div className="cf-stamp">CASE CLOSED</div>

        <h1>{renderTitle(puzzle.title)}</h1>
        <p className="cf-series-note">
          <span>{difficultyLabel(puzzle.difficulty)}</span>
          {' · '}
          {result.wasCorrect
            ? `${result.points} point${result.points === 1 ? '' : 's'}${result.cleanWin ? ' · clean solve' : ` · ${result.hintsUsed} hint${result.hintsUsed === 1 ? '' : 's'} used`}`
            : 'Struck out · 0 points'}
        </p>

        <div className={`cf-verdict ${result.wasCorrect ? 'right' : 'wrong'}`}>{renderEmphasis(verdict)}</div>

        <h2>The Solution</h2>
        <div className="cf-solution">
          {solution.map((paragraph, index) => (
            <p key={index}>{renderEmphasis(paragraph)}</p>
          ))}
        </div>

        <p className="cf-streak-message">{streakMessage}</p>

        {player ? (
          <>
            <div className="cf-member">
              You're <strong>{player.nickname}</strong>, ranked <strong>{player.rank.name}</strong> — {player.wins} wins, {player.points} points, current streak {player.currentStreak}.
            </div>
            <ShareButton {...shareProps} streak={player.currentStreak} />
            <p className="cf-back-links">
              <Link to="/standings">See the standings</Link> · <Link to="/my-record">Your full record</Link>
            </p>
          </>
        ) : (
          <>
            <CaseJoin guid={puzzle.guid} />
            <ShareButton {...shareProps} streak={0} />
          </>
        )}

        <p className="cf-back">
          <Link to="/case-file">Back to the case file</Link>
        </p>
      </main>
    </div>
  );
};

export default CaseAnswer;
